import { getStorageService } from '../../../api_services/StorageService';
import { isCancelledByUserError } from '../../../errors/createCanceledError';
import * as Sentry from '@sentry/browser';
import { sendFilePartUploadMetrics } from './metrics/sendFilePartUploadMetrics';
import { QueuedIntakeFileInfo, UploadData } from '../../../store/uploadStore';
import { UploadNotRestoredError } from '../../../errors/UploadNotRestoredError';
import { sendFileUploadMetrics } from './metrics/sendFileUploadMetrics';
import { cacheUploadedSize } from '../../../store/optimizations/cacheUploadedSize';

const MULTIPART_UPLOAD_THRESHOLD_BYTES = MultiplePartUploadMinFileSizeMB * 1024 * 1024;
const THROTTLE_FILE_PROGRESS_MS = 500;

export const uploadFileToStorage = async (
  upload: UploadData,
  file: QueuedIntakeFileInfo,
  uploadStartTimeMs: number,
  updateUpload: () => void,
) => {
  if (!upload.upload) throw new UploadNotRestoredError();
  if (!file.file) throw new Error('No file found. Something is wrong with the FileUpload component.');

  const uploadPath = `${upload.upload.uri}/${file.name}`;
  const { customer, site } = upload.upload;

  let isThrottled = false;
  const throttledUpdate = () => {
    if (!isThrottled) {
      cacheUploadedSize(upload);
      updateUpload();
      isThrottled = true;
      setTimeout(() => {
        isThrottled = false;
      }, THROTTLE_FILE_PROGRESS_MS);
    }
  };

  if (file.size! > MULTIPART_UPLOAD_THRESHOLD_BYTES) {
    // Do multipart upload
    const partRetries = {};
    const { assembleSec, partsCount } = await getStorageService().multiPartUpload({
      signal: upload.meta.abortController.signal,
      customerId: customer,
      siteId: site,
      uploadPath,
      file: file.file,
      excludeCustomerSitePath: true,
      onPartRetry: (index, retryCountInner, error) => {
        partRetries[index] = retryCountInner;
        if (!isCancelledByUserError(error)) {
          console.error(`Error during file upload (request will be retried): ${error}; retryCount: ${retryCountInner}`);
          Sentry.captureException(error);
        }
      },
      onPartUploaded: (partIndex, partUploadStartTimeMs, size) => {
        sendFilePartUploadMetrics(upload, size, partUploadStartTimeMs, partRetries[partIndex] ?? 0);
      },
      onOverallUploadProgress: (loaded) => {
        file.uploadedSize = loaded;
        throttledUpdate();
      },
    });

    sendFileUploadMetrics(upload, file.size!, uploadStartTimeMs, assembleSec, partsCount);
  } else {
    // Do whole file upload
    let retryCount = 0;
    await getStorageService().upload({
      signal: upload.meta.abortController.signal,
      customerId: customer,
      siteId: site,
      uploadPath,
      file: file.file,
      excludeCustomerSitePath: true,
      onRetry: (retryCountInner, error) => {
        if (!isCancelledByUserError(error)) {
          console.error(`Error during file upload (request will be retried): ${error}; retryCount: ${retryCountInner}`);
          Sentry.captureException(error);
        }
        retryCount = retryCountInner;
      },
      onUploadProgress: (event) => {
        file.uploadedSize = event.loaded;
        throttledUpdate();
      },
    });

    sendFilePartUploadMetrics(upload, file.size!, uploadStartTimeMs, retryCount);
    sendFileUploadMetrics(upload, file.size!, uploadStartTimeMs);
  }

  // Always call update one final time to ensure 100% is shown
  file.uploadedSize = file.size!;
  cacheUploadedSize(upload);
  updateUpload();
};
